import React, { createContext, useState, useContext, useEffect, useMemo } from 'react';

const DeliveryAddressContext = createContext();

export const useDeliveryAddress = () => {
  const context = useContext(DeliveryAddressContext);
  if (!context) {
    throw new Error('useDeliveryAddress must be used within a DeliveryAddressProvider');
  }
  return context;
};

export const DeliveryAddressProvider = ({ children }) => {
  const [currentAddress, setCurrentAddressState] = useState(null);
  const [addressVersion, setAddressVersion] = useState(0);

  useEffect(() => {
    // Load saved address from localStorage
    const saved = localStorage.getItem('currentDeliveryAddress');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Ensure coordinates are numbers, not strings
        if (parsed?.coordinates?.lat != null && parsed?.coordinates?.lng != null) {
          parsed.coordinates = {
            lat: parseFloat(parsed.coordinates.lat),
            lng: parseFloat(parsed.coordinates.lng)
          };
        }
        setCurrentAddressState(parsed);
      } catch (error) {
        console.error('Error loading saved address:', error);
      }
    }
  }, []);

  useEffect(() => {
    // Save address to localStorage when it changes
    if (currentAddress) {
      try {
        localStorage.setItem('currentDeliveryAddress', JSON.stringify(currentAddress));
      } catch (error) {
        console.error('Error saving address to localStorage:', error);
      }
    }
  }, [currentAddress]);

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    currentAddress,
    addressVersion,
    setCurrentAddress: (address) => {
      if (address) {
        const lat = parseFloat(address.coordinates?.lat);
        const lng = parseFloat(address.coordinates?.lng);
        const newAddress = {
          label: address.label || '',
          street: address.street || '',
          city: address.city || '',
          zipCode: address.zipCode || '',
          addressDetails: address.addressDetails || '',
          deliveryInstructions: address.deliveryInstructions || '',
          coordinates: {
            lat: (lat != null && !isNaN(lat)) ? lat : null,
            lng: (lng != null && !isNaN(lng)) ? lng : null
          }
        };
        setCurrentAddressState(newAddress);
        setAddressVersion((v) => v + 1);
      } else {
        setCurrentAddressState(null);
        setAddressVersion((v) => v + 1);
      }
    }
  }), [currentAddress, addressVersion]);

  return (
    <DeliveryAddressContext.Provider value={contextValue}>
      {children}
    </DeliveryAddressContext.Provider>
  );
};
